/**
 * ProgramLayout
 * Layout for when user is INSIDE a specific program
 * Uses unified top nav with program context and left-side agent
 */

import { ReactNode, useEffect, useState } from 'react'
import { Link, Outlet, useParams, useLocation } from 'react-router-dom'
import AppShell, { type TabItem } from '../components/ui/AppShell'
import UserMenu from '../components/ui/UserMenu'
import RecentProgramsNav from '../components/RecentProgramsNav'
import { PM_NAV } from './navConfig'
import { useTypedNavigate, ROUTES } from '../lib/routing'
import { tokenManager } from '../lib/api'
import { useUser } from '../contexts/UserContext'
import { useAgent } from '../contexts/AgentContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface Program {
  id: number
  name: string
  status: string
  status_display: string
}

interface ProgramLayoutProps {
  children?: ReactNode
}

const ProgramLayout = ({ children }: ProgramLayoutProps) => {
  const [program, setProgram] = useState<Program | null>(null)
  const navigate = useTypedNavigate()
  const { programId } = useParams<{ programId: string }>()
  const { pathname } = useLocation()
  const { user, logout } = useUser()
  const { updatePageContext } = useAgent()

  const handleLogout = () => {
    logout()
    navigate(ROUTES.public.login)
  }

  // Fetch program data and chat history
  const fetchProgramData = async () => {
    if (!programId) return
    
    try {
      const token = tokenManager.getAccessToken()
      if (!token) {
        navigate(ROUTES.public.login)
        return
      }

      // Fetch program details
      const programResponse = await fetch(`${API_BASE_URL}/api/programs/${programId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      })

      if (programResponse.ok) {
        const programData = await programResponse.json()
        setProgram(programData)
      }

      // Chat history is now managed by the global agent drawer
      // No need to fetch it here anymore
    } catch (error) {
      console.error('Error fetching program data:', error)
    }
  }

  // Initial fetch when programId changes
  useEffect(() => {
    fetchProgramData()
  }, [programId])

  // Tier 1: Enrich agent metadata with program name/status and user role
  useEffect(() => {
    if (program) {
      updatePageContext({
        programName: program.name,
        programStatus: program.status_display || program.status,
      })
    }
  }, [program, updatePageContext])

  useEffect(() => {
    if (user?.role) {
      updatePageContext({ userRole: user.role })
    }
  }, [user?.role, updatePageContext])

  // Listen for refresh events from child components
  useEffect(() => {
    const handleRefresh = () => {
      console.log('Refreshing program data...')
      fetchProgramData()
    }

    window.addEventListener('refreshProgramData', handleRefresh)
    return () => window.removeEventListener('refreshProgramData', handleRefresh)
  }, [programId])

  const programName = program ? program.name : 'Loading...'

  // Market is a grouped section spanning three sub-pages (Solutions / Market
  // Search / Vendor Shortlist). The group tab stays active across all three and
  // reveals the secondary sub-tab row below.
  const marketPaths = programId
    ? [
        `/programs/${programId}/solutions`,
        `/programs/${programId}/market`,
        `/programs/${programId}/vendors`,
      ]
    : []
  const inMarket = marketPaths.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  )

  // DEV-732: the module graph (program index route) is the primary program nav
  // now — the old fixed tabs (Overview / Requirements / Documents / Market) are
  // retired. Their pages remain reachable as module review surfaces (deep-linked
  // from each module page). The breadcrumb's program name links back to the graph.
  const tabs: TabItem[] = []

  const subtabs: TabItem[] | undefined =
    programId && inMarket
      ? [
          { label: 'Solutions', to: `/programs/${programId}/solutions` },
          { label: 'Market Search', to: `/programs/${programId}/market` },
          { label: 'Vendor Shortlist', to: `/programs/${programId}/vendors` },
        ]
      : undefined

  return (
    <AppShell
      nav={PM_NAV}
      sidebarExtra={() => <RecentProgramsNav />}
      footer={(collapsed) => (
        <UserMenu
          collapsed={collapsed}
          onLogout={handleLogout}
          userName={user?.full_name || 'User'}
          userOfficeName={user?.office?.name || 'Office'}
        />
      )}
      title={
        // Just the program title; clicking it returns to the program overview
        // (the module workflow graph). Breadcrumb dropped for simplicity (DEV-732).
        programId ? (
          <Link to={`/programs/${programId}`} className="hover:opacity-80 transition-opacity">
            {programName}
          </Link>
        ) : (
          programName
        )
      }
      tabs={tabs}
      subtabs={subtabs}
    >
      {children || <Outlet />}
    </AppShell>
  )
}

export default ProgramLayout

